import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, Typography, Table, TableBody, TableCell, TableContainer, TableRow, IconButton, Link, Grid, Button, useMediaQuery, Menu, MenuItem, ListItemIcon, Stack } from '@mui/material';
import { Loading } from '../../../../components/loading/Loading';
import { organizationEmployeeSnapshotListener } from '../../../../repository/EmployeeDAO';
import { useUserContext } from '../../../../context/user_context/UserContext';
import EditEmployeeModal from './modals/EditEmployeeModal';
import AppSidebar from '../../../../components/navbar/DesktopAppSidebar';
import { AttachFile, Edit, Delete, MoreVert, WatchLater } from '@mui/icons-material';
import { employeeDocumentsSnapshotListener, deleteEmployeeDocumentAndReferenceFromFirestore, getEmployeeDocumentDownloadURL } from '../../../../repository/DocumentsDAO';
import AddDocumentModal from '../../../../components/documents/modals/AddDocumentModal';
import { formatFirestoreTimestampMMDDYYYY } from '../../../../utils/time_utils/TimeUtils';
import { useToast, ToastType } from '../../../../context/toast/ToastContext';
import AvailabilityModal from './modals/AvailabilityModal';
import ConfirmDeleteModal from '../../../../components/modals/ConfirmDeleteModal';
import usePermissions from '../../../../utils/permissions/UsePermissions';
import { Modules } from '../../../../utils/permissions/Modules';
import { PermissionTypes } from '../../../../utils/permissions/PermissionTypes';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../../resources/strings/StringKeys';
import { useOrganizationContext } from '../../../../context/organization_context/OrganizationContext';
import AvailabilityTable from '../../../../components/availability/AvailabilityTable';
import { handleMenuClick, handleMenuClose } from '../../../../components/utils/MenuHandlers';

function EmployeeDetail() {
    const { employeeId } = useParams();
    const [employeeData, setEmployeeData] = useState();
    const [showEmployeeToEditModal, setShowEmployeeToEditModal] = useState(false);
    const [documents, setDocuments] = useState([]);
    const { firebaseUser } = useUserContext();
    const [showAddDocumentModal, setShowAddDocumentModal] = useState(false);
    const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
    const { showToast } = useToast();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [docToDelete, setDocToDelete] = useState(null);
    const { t } = useTranslation();
    const { hasPermission } = usePermissions();
    const isMobile = useMediaQuery('(max-width:600px)');
    const [anchorEl, setAnchorEl] = useState(null);
    const { getDepartmentByID } = useOrganizationContext();
    const navigate = useNavigate();

    useEffect(() => {
        if (firebaseUser) {
            const unsubscribe = employeeDocumentsSnapshotListener(firebaseUser.organization, employeeId, setDocuments);
            return () => unsubscribe();
        }
    }, [firebaseUser, employeeId]);

    const handleDeleteDocument = async () => {
        setConfirmOpen(false);
        try {
            const result = await deleteEmployeeDocumentAndReferenceFromFirestore(
                firebaseUser.organization,
                employeeId,
                docToDelete.id,
                docToDelete.name
            );
    
            if (result.success) showToast(t(StringKeys.SUCCESSFULLY_DELETED_X, {x:`${docToDelete.name}`}), ToastType.SUCCESS);
            else showToast(t(StringKeys.FAILED_TO_DELETE_X, {x:`${docToDelete.name}`}), ToastType.SUCCESS);

        } catch (error) {
            showToast(t(StringKeys.FAILED_TO_DELETE_X, {x:`${docToDelete.name}`}), ToastType.SUCCESS);
        }
    };
    
    useEffect(() => {
        if (employeeId && firebaseUser) {
            const unsubscribe = organizationEmployeeSnapshotListener(firebaseUser.organization, employeeId, setEmployeeData);
            return () => unsubscribe();
        }
    }, [employeeId, firebaseUser]);

    if (!employeeData) return <AppSidebar content={<Loading />} />;

    return (
        <>
            <AppSidebar
                content={
                    <Stack gap={4}>
                        <Grid container alignItems="center" spacing={2}>
                            <Grid item>
                                {employeeData.profile_picture_url ? (
                                    <img src={employeeData.profile_picture_url} alt="Profile" style={{
                                        width: '70px', 
                                        height: '70px',
                                        objectFit: 'cover',
                                        cursor: "pointer"
                                    }} />
                                ) : (
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        backgroundColor: '#007bff',
                                        color: 'white',
                                        fontWeight: 'bold',
                                        width: '70px',
                                        height: '70px',
                                        cursor: "pointer"
                                    }}>
                                        {employeeData.first_name[0].toUpperCase()}{employeeData.last_name[0].toUpperCase()}
                                    </div>
                                )}
                            </Grid>

                            {!isMobile && hasPermission(Modules.EMPLOYEES, PermissionTypes.WRITE) && (
                                <Grid item>
                                    <Stack direction="row" spacing={2}>
                                        <Button variant="text" startIcon={<Edit />} onClick={() => setShowEmployeeToEditModal(true)}>
                                                {t(StringKeys.EDIT)}
                                        </Button>
                                        {/* <Button variant="text" startIcon={<AttachFile />} onClick={() => setShowAddDocumentModal(true)}>
                                                {t(StringKeys.ADD_DOCUMENT)}
                                        </Button> */}
                                        <Button variant="text" startIcon={<WatchLater />} onClick={() => setShowAvailabilityModal(true)}>
                                                {t(StringKeys.AVAILABILITY)}
                                        </Button>                                        
                                    </Stack>
                                </Grid>
                            )}
                            {isMobile && hasPermission(Modules.EMPLOYEES, PermissionTypes.WRITE) && (
                                <Grid item xs="auto">
                                    <IconButton onClick={(event) => handleMenuClick(event, setAnchorEl)}>
                                        <MoreVert />
                                    </IconButton>
                                    <Menu
                                        anchorEl={anchorEl}
                                        open={Boolean(anchorEl)}
                                        onClose={() => handleMenuClose(setAnchorEl)}
                                    >
                                        <MenuItem onClick={() => { setShowEmployeeToEditModal(true); handleMenuClose(setAnchorEl); }}>
                                            <ListItemIcon>
                                                <Edit fontSize="small" />
                                            </ListItemIcon>
                                            {t(StringKeys.EDIT)}
                                        </MenuItem>
                                        {/* <MenuItem onClick={() => { setShowAddDocumentModal(true); handleMenuClose(); }}>
                                            <ListItemIcon>
                                                <AttachFile fontSize="small" />
                                            </ListItemIcon>
                                            {t(StringKeys.ADD_DOCUMENT)}
                                        </MenuItem> */}
                                        <MenuItem onClick={() => { setShowAvailabilityModal(true); handleMenuClose(setAnchorEl); }}>
                                            <ListItemIcon>
                                                <WatchLater fontSize="small" />
                                            </ListItemIcon>
                                            {t(StringKeys.AVAILABILITY)}
                                        </MenuItem>
                                    </Menu>
                                </Grid>
                            )}
                        </Grid>

                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant={isMobile ? "h6" : "h5"} component="div">{employeeData.first_name} {employeeData.last_name}</Typography>
                                {/* <Typography>
                                    {t(StringKeys.TITLE)}: {employeeData.title}
                                </Typography> */}
                                <Typography>
                                    {t(StringKeys.DEPARTMENT)}:{' '}
                                        {(() => {
                                            const department = getDepartmentByID(employeeData.department_id);
                                            if (!department) return t(StringKeys.UNKNOWN);
                                            return hasPermission(Modules.DEPARTMENTS, PermissionTypes.READ) ? 
                                                (
                                                    <Link
                                                        onClick={() => navigate(`/departments/${employeeData.department_id}`)}
                                                        sx={{ cursor: 'pointer' }}
                                                    >
                                                        {`${department.name}`}
                                                    </Link>
                                                ) : (
                                                    department.name
                                                );
                                        })()}
                                </Typography>                                
                                <Typography>
                                    {t(StringKeys.ROLE)}: {employeeData.role ? (employeeData.role) : t(StringKeys.UNASSIGNED)}
                                </Typography>
                                <Typography>
                                    {t(StringKeys.HIRE_DATE)}: {formatFirestoreTimestampMMDDYYYY(employeeData.hire_date)}
                                </Typography>
                                <Typography>
                                    {t(StringKeys.ACTIVE)}: {employeeData.active ? t(StringKeys.YES) : t(StringKeys.NO)}
                                </Typography>
                                {employeeData.termination_date && (
                                    <Typography>
                                        {t(StringKeys.TERMINATION_DATE)}: {formatFirestoreTimestampMMDDYYYY(employeeData.termination_date)}
                                    </Typography>
                                )}
                                <Typography>
                                    {t(StringKeys.PHONE)}: {employeeData.phone_number}
                                </Typography>
                                <Typography>
                                    {t(StringKeys.EMAIL)}: {employeeData.email}
                                </Typography>
                                {hasPermission(Modules.EMPLOYEES, PermissionTypes.VIEW_SALARY_INFO) && (
                                    <Typography>
                                        {t(StringKeys.RATE)}: ${employeeData.rate}
                                    </Typography>
                                )}
                                <Typography>
                                    {t(StringKeys.BIRTHDAY)}: {formatFirestoreTimestampMMDDYYYY(employeeData.birthday)}
                                </Typography>
                            </CardContent>
                        </Card>

                        {/* <Card variant="outlined">
                            <CardContent>
                                <Typography variant={isMobile ? "h6" : "h5"} component="div">{t(StringKeys.DOCUMENTS)}</Typography>
                                <TableContainer>
                                    <Table aria-label="documents-table">
                                        <TableBody>
                                            {documents.length > 0 ? (
                                                documents.map((doc) => (
                                                    <TableRow key={doc.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                        <TableCell component="th" scope="row">
                                                            <Link 
                                                                onClick={async (e) => {
                                                                    e.preventDefault();
                                                                    try {
                                                                        var result = await getEmployeeDocumentDownloadURL(firebaseUser.organization, firebaseUser.uid, doc.id)
                                                                        if (result.success) {
                                                                            window.open(result.data, '_blank');
                                                                        } else {
                                                                            showToast(t(StringKeys.FAILED_TO_LOAD_DOCUMENT), ToastType.DANGER);
                                                                        }
                                                                    } catch (error) {
                                                                        console.log(error)
                                                                        showToast(t(StringKeys.FAILED_TO_LOAD_DOCUMENT), ToastType.DANGER);
                                                                    }
                                                                }}
                                                                sx={{ cursor: 'pointer' }}
                                                            >
                                                                {doc.name}
                                                            </Link>
                                                        </TableCell>
                                                        {hasPermission(Modules.EMPLOYEES, PermissionTypes.WRITE) && (
                                                            <TableCell align="right">
                                                                <IconButton onClick={() => { setDocToDelete(doc); setConfirmOpen(true); }} aria-label="delete">
                                                                    <Delete />
                                                                </IconButton>
                                                            </TableCell>
                                                        )}
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableContainer>
                                                    <Table aria-label="no-notes-table">
                                                        <TableBody>
                                                            <TableRow>
                                                                <TableCell colSpan={3} align="center">
                                                                    <Typography variant="body2">{t(StringKeys.NO_DOCUMENTS_TO_SHOW)}</Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                            </CardContent>
                        </Card> */}

                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant={isMobile ? "h6" : "h5"}>{t(StringKeys.AVAILABILITY)}</Typography>
                                <AvailabilityTable availability={employeeData.availability} />
                            </CardContent>
                        </Card>

                        <EditEmployeeModal
                            employee={employeeData}
                            show={showEmployeeToEditModal}
                            onHide={() => {
                                setShowEmployeeToEditModal(false);
                            }}
                        />
                        <AddDocumentModal
                            show={showAddDocumentModal}
                            onHide={() => setShowAddDocumentModal(false)}
                            organization={firebaseUser.organization}
                            type={"EmployeeDocument"}
                            employee={employeeId}
                        />
                        <AvailabilityModal
                            show={showAvailabilityModal}
                            onHide={() => setShowAvailabilityModal(false)}
                            availability={employeeData.availability}
                            employeeId={employeeId}
                        />
                        <ConfirmDeleteModal
                            open={confirmOpen}
                            onClose={() => setConfirmOpen(false)}
                            onConfirm={() => handleDeleteDocument()}
                            item={docToDelete ? docToDelete.title : null}
                        />
                    </Stack>
                }
            />
        </>
    );
}

export default EmployeeDetail;
